import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface UseTileZoomOptions {
    disabled?: boolean
    minScale?: number
    maxScale?: number
    step?: number
}

export function useTileZoom({
    disabled = false,
    minScale = 1,
    maxScale = 5,
    step = 0.25
}: UseTileZoomOptions = {}) {
    const containerRef = useRef<HTMLDivElement>(null)
    const [scale, setScale] = useState(1)
    const [pan, setPan] = useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)
    const dragRef = useRef<{
        startX: number
        startY: number
        panX: number
        panY: number
        moved: boolean
    } | null>(null)
    /**
     * Quadro agendado pra aplicar o arrasto, e a ultima posicao lida.
     *
     * O ponteiro dispara `pointermove` na taxa do DISPOSITIVO — um mouse gamer
     * passa de 1000 eventos por segundo. Chamando `setPan` em cada um, o tile
     * inteiro (video, controles, minimapa) re-renderizava mil vezes por segundo
     * pra alimentar uma tela que atualiza 60. Guardar a ultima posicao e
     * aplicar uma vez por quadro entrega exatamente a mesma imagem por uma
     * fracao do trabalho — e o arrasto fica MAIS suave, nao menos, porque a
     * thread para de se afogar em renders que ninguem chega a ver.
     */
    const frameRef = useRef<number | null>(null)
    const pointerRef = useRef({ x: 0, y: 0 })

    const cancelFrame = useCallback(() => {
        if (frameRef.current === null) return
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
    }, [])

    // Desmontar no meio de um arrasto deixaria um quadro agendado mexendo em
    // estado de componente que ja saiu.
    useEffect(() => cancelFrame, [cancelFrame])

    // Reseta o zoom se for desabilitado (ex: miniatura na tira)
    useEffect(() => {
        if (disabled && scale !== 1) {
            setScale(1)
            setPan({ x: 0, y: 0 })
        }
    }, [disabled, scale])

    const clampPan = useCallback((x: number, y: number, currentScale: number) => {
        const container = containerRef.current
        if (!container || currentScale <= 1) return { x: 0, y: 0 }
        const rect = container.getBoundingClientRect()
        const maxPanX = (rect.width * (currentScale - 1)) / 2
        const maxPanY = (rect.height * (currentScale - 1)) / 2
        return {
            x: Math.max(-maxPanX, Math.min(maxPanX, x)),
            y: Math.max(-maxPanY, Math.min(maxPanY, y))
        }
    }, [])

    const resetZoom = useCallback(() => {
        setScale(1)
        setPan({ x: 0, y: 0 })
    }, [])

    const setScaleDirect = useCallback((newScale: number) => {
        if (disabled) return
        const targetScale = Math.max(minScale, Math.min(maxScale, Math.round(newScale * 100) / 100))
        setScale(targetScale)
        if (targetScale <= 1) {
            setPan({ x: 0, y: 0 })
        } else {
            setPan((prev) => clampPan(prev.x, prev.y, targetScale))
        }
    }, [disabled, minScale, maxScale, clampPan])

    const panToNormalized = useCallback((normX: number, normY: number) => {
        if (disabled || scale <= 1 || !containerRef.current) return
        const rect = containerRef.current.getBoundingClientRect()
        const targetPanX = (0.5 - normX) * (rect.width * scale)
        const targetPanY = (0.5 - normY) * (rect.height * scale)
        setPan(clampPan(targetPanX, targetPanY, scale))
    }, [disabled, scale, clampPan])

    const zoomBy = useCallback((delta: number, focalPoint?: { clientX: number; clientY: number }) => {
        if (disabled) return
        setScale((prevScale) => {
            const nextScale = Math.max(minScale, Math.min(maxScale, Math.round((prevScale + delta) * 100) / 100))
            if (nextScale === 1) {
                setPan({ x: 0, y: 0 })
                return 1
            }
            if (focalPoint && containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect()
                const offsetX = focalPoint.clientX - rect.left - rect.width / 2
                const offsetY = focalPoint.clientY - rect.top - rect.height / 2
                setPan((prevPan) => {
                    const nextX = offsetX - (offsetX - prevPan.x) * (nextScale / prevScale)
                    const nextY = offsetY - (offsetY - prevPan.y) * (nextScale / prevScale)
                    return clampPan(nextX, nextY, nextScale)
                })
            } else {
                setPan((prevPan) => clampPan(prevPan.x, prevPan.y, nextScale))
            }
            return nextScale
        })
    }, [disabled, minScale, maxScale, clampPan])

    const zoomIn = useCallback(() => {
        zoomBy(step)
    }, [zoomBy, step])

    const zoomOut = useCallback(() => {
        zoomBy(-step)
    }, [zoomBy, step])

    const onWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
        if (disabled) return
        // Ignora se nao houve rolagem
        if (event.deltaY === 0 && !event.ctrlKey) return

        // Previne rolagem da pagina ao dar zoom no video
        event.preventDefault()
        event.stopPropagation()

        // Trackpad pinch (ctrlKey) ou scroll do mouse
        const delta = event.ctrlKey
            ? -event.deltaY * 0.05
            : (event.deltaY < 0 ? step : -step)

        zoomBy(delta, { clientX: event.clientX, clientY: event.clientY })
    }, [disabled, step, zoomBy])

    const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (disabled || scale <= 1 || event.button !== 0) return
        // Ignora cliques que vierem de botoes ou controles interativos
        if ((event.target as HTMLElement).closest('button, [role="button"], a, input')) return

        event.preventDefault()
        event.stopPropagation()
        const target = event.currentTarget
        target.setPointerCapture(event.pointerId)
        // Ancora a posicao logo na descida: um clique que solta sem mover
        // fecharia o arrasto lendo a coordenada de um arrasto ANTERIOR, e o
        // tile saltava sozinho.
        pointerRef.current = { x: event.clientX, y: event.clientY }
        dragRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            panX: pan.x,
            panY: pan.y,
            moved: false
        }
        setIsDragging(true)
    }, [disabled, scale, pan])

    const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragRef.current || scale <= 1) return
        event.preventDefault()

        // Lido AGORA, sincronamente: o evento nao sobrevive ate o quadro.
        pointerRef.current = { x: event.clientX, y: event.clientY }

        const dx = event.clientX - dragRef.current.startX
        const dy = event.clientY - dragRef.current.startY
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
            dragRef.current.moved = true
        }

        // Ja ha quadro a caminho: ele vai ler a posicao mais recente.
        if (frameRef.current !== null) return
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null
            const drag = dragRef.current
            if (!drag) return
            const nextX = drag.panX + (pointerRef.current.x - drag.startX)
            const nextY = drag.panY + (pointerRef.current.y - drag.startY)
            setPan(clampPan(nextX, nextY, scale))
        })
    }, [scale, clampPan])

    const onPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (dragRef.current) {
            try {
                event.currentTarget.releasePointerCapture(event.pointerId)
            } catch {
                // ignorado se o pointer capture ja foi liberado
            }
            // Fecha na posicao exata em que o dedo saiu, sem esperar o quadro
            // pendente: solto o arrasto, `dragRef` some e ele sairia calado.
            const drag = dragRef.current
            cancelFrame()
            setPan(clampPan(
                drag.panX + (pointerRef.current.x - drag.startX),
                drag.panY + (pointerRef.current.y - drag.startY),
                scale
            ))
            dragRef.current = null
            setIsDragging(false)
        }
    }, [cancelFrame, clampPan, scale])

    const viewportBox = useMemo(() => {
        if (scale <= 1) {
            return { left: '0%', top: '0%', width: '100%', height: '100%' }
        }
        const container = containerRef.current
        const width = container?.clientWidth || 1
        const height = container?.clientHeight || 1

        const widthPercent = Math.min(100, (1 / scale) * 100)
        const heightPercent = Math.min(100, (1 / scale) * 100)

        const cx = 0.5 - pan.x / (width * scale)
        const cy = 0.5 - pan.y / (height * scale)

        const leftPercent = (cx - 0.5 / scale) * 100
        const topPercent = (cy - 0.5 / scale) * 100

        const clampedLeft = Math.max(0, Math.min(100 - widthPercent, leftPercent))
        const clampedTop = Math.max(0, Math.min(100 - heightPercent, topPercent))

        return {
            left: `${clampedLeft}%`,
            top: `${clampedTop}%`,
            width: `${widthPercent}%`,
            height: `${heightPercent}%`
        }
    }, [scale, pan])

    return {
        containerRef,
        scale,
        pan,
        isDragging,
        isZoomed: scale > 1,
        viewportBox,
        zoomIn,
        zoomOut,
        zoomBy,
        setScaleDirect,
        panToNormalized,
        resetZoom,
        onWheel,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel: onPointerUp,
        hasDragged: () => dragRef.current?.moved ?? false
    }
}
